import _ from 'lodash';
import { TFunction } from 'i18next';
import React, { useRef, useState } from 'react';

import {
  IDownloadResultsPackageForUI,
  ResultsPackageReason,
  IReportSummaryAPIResponse,
  IDownloadResultsPackageAPIResponse,
  ClinicalReportStatus,
} from '@models/results';

import { formatDateWithTimezone } from '@utils';
import { getDownloadResultsPackage } from '@services/reports';
import { TableReturnState } from '@hooks/useTableHook';

export const parseDownloadResultsPackageForUI = (
  summaries: IReportSummaryAPIResponse[]
): IDownloadResultsPackageForUI[] => {
  return _(summaries)
    .groupBy((summary) => summary.order.orderNumber)
    .map((value, key) => ({
      orderId: key,
      orderDescription: value[0].order.description,
      summaries: value,
    }))
    .value();
};

export const useParsedSummaryData = (id: string) => {
  const [_requestStatus, setRequestStatus] = useState<number>(0);

  const downloadResultValue =
    useRef<IDownloadResultsPackageAPIResponse | Error | null>(null);

  React.useEffect(() => {
    setRequestStatus(1);
    getDownloadResultsPackage(id)
      .then((data) => {
        downloadResultValue.current = data;
        setRequestStatus(2);
      })
      .catch((err: Response) => {
        downloadResultValue.current = new Error(`Error status ${err.status}`);
        setRequestStatus(3);
      });
  }, []);

  return {
    requestStatus: _requestStatus,
    summaryDetail: downloadResultValue.current,
    summaryList: React.useMemo(
      () =>
        downloadResultValue.current === null ||
        downloadResultValue.current instanceof Error
          ? []
          : parseDownloadResultsPackageForUI(
              (
                downloadResultValue.current as IDownloadResultsPackageAPIResponse
              ).reportSummaries
            ),
      [downloadResultValue.current]
    ),
  };
};

export interface PackageSummaryLocalDataList {
  key: string;
  title: string;
  colWidth: string;
}

export const packageSummaryDataList: PackageSummaryLocalDataList[] = [
  {
    key: 'details',
    title: 'downloadResultsPkg_summary_pkgDetails',
    colWidth: '3',
  },
  {
    key: 'name',
    title: 'downloadResultsPkg_summary_pkgName',
    colWidth: '4',
  },
  {
    key: 'sizeInBytes',
    title: 'downloadResultsPkg_summary_pkgSize',
    colWidth: '2',
  },
  {
    key: 'created',
    title: 'downloadResultsPkg_summary_pkgCreated',
    colWidth: '3',
  },
];

export type IPackageSummaryDetailsKeys =
  | 'details'
  | 'name'
  | 'sizeInBytes'
  | 'created'
  | 'reason';

export const parsePackageSizeToMB = (value: number) => {
  value /= 1000000;
  return value.toFixed(1);
};

export const getReasonForCreationTranslationToken = (
  value: ResultsPackageReason
) => {
  const tokenMap = {
    NA: 'downloadResultsPkg_summary_pkgUnknownReason',
    Manual: 'downloadResultsPkg_summary_pkgManualReason',
    OrderCompletion: 'downloadResultsPkg_summary_pkgFinishedReason',
  };
  return tokenMap[value];
};

export const getPackageReasonTextSet = (
  orderNumber: string,
  summaryList: IReportSummaryAPIResponse[]
) => {
  const foundPackageSummary = _.find(
    summaryList,
    (item) => item.order.id === orderNumber
  );
  if (foundPackageSummary) {
    return {
      orderNumber: foundPackageSummary.order.orderNumber,
      orderDescription: foundPackageSummary.order.description,
    };
  }
  return {
    orderNumber: null,
    orderDescription: null,
  };
};

export const countInactiveSamples = (
  sampleSummaries: IReportSummaryAPIResponse[]
) => {
  return _.filter(sampleSummaries, (item) => {
    const { status } = item.clinicalReport;
    return status === 'Obsolete';
  }).length;
};

export const parseReasonForCreationData = (
  downloadResultsPkg: IDownloadResultsPackageAPIResponse,
  t: TFunction
) => {
  const reasonValue = downloadResultsPkg.pkgInfo.reason;
  const token = getReasonForCreationTranslationToken(reasonValue);
  if (reasonValue === 'Manual' || reasonValue === 'NA') {
    return t(token) as string;
  } else if (reasonValue === 'OrderCompletion') {
    const { orderDescription, orderNumber } = getPackageReasonTextSet(
      downloadResultsPkg!.pkgInfo.reasonOrderId!,
      downloadResultsPkg!.reportSummaries
    );
    return orderNumber && orderDescription
      ? (t(token, {
          orderNumber,
          orderDescription,
        }) as string)
      : '-';
  }
};

export const parseSummaryInfoValues = (
  item: PackageSummaryLocalDataList,
  data: IDownloadResultsPackageAPIResponse,
  summaryListLength: number,
  timezone: string,
  t: TFunction
) => {
  const summaryKey = item.key as IPackageSummaryDetailsKeys;
  let value: Date | number | string =
    data.pkgInfo![summaryKey] ?? ('-' as string);
  switch (summaryKey) {
    case 'details': {
      const summarySamplesQty = data!.reportSummaries.length;
      const ordersQty = summaryListLength;
      value = t('downloadResultsPkg_summary_pkgDetailsValue', {
        sampleText:
          summarySamplesQty > 1 ? 'common_lc_samples' : 'common_lc_sample',
        summarySamplesQty,
        ordersQty,
        orderText: ordersQty > 1 ? 'common_lc_orders' : 'common_lc_order',
      }) as string;
      break;
    }
    case 'sizeInBytes': {
      value = t('downloadResultsPkg_summary_pkgSizeConverted', {
        pkgSize: parsePackageSizeToMB(data.pkgInfo![summaryKey]),
      }) as string;
      break;
    }
    case 'created': {
      value = formatDateWithTimezone(
        new Date(data.pkgInfo![summaryKey]),
        timezone
      );
      break;
    }
    default:
      break;
  }
  return value;
};

export const getInactiveReportStatuses = (
  reportStatus: ClinicalReportStatus,
  hasActiveReport: boolean
) => {
  const isInactiveReport = reportStatus === 'Obsolete';
  const isObsoleteReport = isInactiveReport && !hasActiveReport;
  const isOutdatedReport = isInactiveReport && hasActiveReport;
  return {
    isObsoleteReport,
    isOutdatedReport,
    isInactiveReport,
  };
};

export interface DownloadResultsTableReturnState extends TableReturnState {
  data: IReportSummaryAPIResponse[];
}

export const mockedDownloadPackageResultsData: IDownloadResultsPackageAPIResponse =
  {
    pkgInfo: {
      id: 'f3bc9a0f-2981-458b-9004-fcceb6d67acb',
      name: 'A3_PKG_20210302_173336.zip',
      created: '2021-03-03T01:33:44.780032Z',
      state: 'ReportAvailable',
      sizeInBytes: 265827,
      reason: 'Manual',
      reasonOrderId: null,
    },
    reportSummaries: [
      {
        order: {
          id: '675f5ad1-ac91-410d-81a7-d7c0816ce263',
          orderNumber: 'R-073258',
          description: 'XTESTSCENARIOX-Erica0302_BMRD3',
          status: 'Completed',
          created: '2021-03-03T01:22:56.807547Z',
        },
        orderTest: {
          id: '6c91fae4-cc47-46c1-a8f3-22261c44c18f',
          sampleName: 'MRDEChang0302_B3',
          description: 'XTESTSCENARIOX-Erica0302_BMRD3',
          testType: 'MRD',
          statusType: 'Completed',
        },
        specimen: {
          specimenNumber: 'SP-898120',
          collectionDate: '2018-08-17',
          externalSubjectId: 'SubjectIdEChang0302_B',
        },
        hasActiveReport: true,
        clinicalReport: {
          id: '2c54d40f-875f-4670-94d2-1bfccb9ae641',
          name: 'A321620094',
          documentType: 'CDx',
          status: 'Active',
          externalSubjectId: 'SubjectIdEChang0302_B',
          orderTestId: '6c91fae4-cc47-46c1-a8f3-22261c44c18f',
          created: '2021-03-03T01:27:14.812204Z',
        },
      },
      {
        order: {
          id: '6cdbdf78-352c-4d6f-a4bb-46c83cac9b26',
          orderNumber: 'R-073257',
          description: 'XTESTSCENARIOX-Erica0302_BMRD2',
          status: 'Completed',
          created: '2021-03-03T01:18:45.587630Z',
        },
        orderTest: {
          id: '35dd8580-27f8-4b73-ad5b-636285d1abb0',
          sampleName: 'MRDEChang0302_B2',
          description: 'XTESTSCENARIOX-Erica0302_BMRD2',
          testType: 'MRD',
          statusType: 'Completed',
        },
        specimen: {
          specimenNumber: 'SP-898119',
          collectionDate: '2018-08-17',
          externalSubjectId: 'SubjectIdEChang0302_B',
        },
        hasActiveReport: false,
        clinicalReport: {
          id: '7913250e-7ff7-41cd-b5d4-7184502ce0cb',
          name: 'A321666103',
          documentType: 'CDx',
          status: 'Obsolete',
          externalSubjectId: 'SubjectIdEChang0302_B',
          orderTestId: '35dd8580-27f8-4b73-ad5b-636285d1abb0',
          created: '2021-03-03T01:21:30.853382Z',
        },
      },
      {
        order: {
          id: '8d585803-4392-41eb-a373-708ba82442c7',
          orderNumber: 'R-073256',
          description: 'XTESTSCENARIOX-Erica0302_BMRD',
          status: 'Completed',
          created: '2021-03-03T01:04:20.809254Z',
        },
        orderTest: {
          id: 'ab1033ac-d1aa-4226-bf73-a02ffc8f6195',
          sampleName: 'MRDEChang0302_B',
          description: 'XTESTSCENARIOX-Erica0302_BMRD',
          testType: 'MRD',
          statusType: 'Completed',
        },
        specimen: {
          specimenNumber: 'SP-898118',
          collectionDate: '2018-08-17',
          externalSubjectId: 'SubjectIdEChang0302_B',
        },
        hasActiveReport: true,
        clinicalReport: {
          id: 'a4c5d2ac-4dbf-446b-9ff9-7dfa3a0d4e2e',
          name: 'A321529382',
          documentType: 'CDx',
          status: 'Active',
          externalSubjectId: 'SubjectIdEChang0302_B',
          orderTestId: 'ab1033ac-d1aa-4226-bf73-a02ffc8f6195',
          created: '2021-03-03T01:11:12.802891Z',
        },
      },
      {
        order: {
          id: 'a564377d-630f-4da7-bb21-0d198429940e',
          orderNumber: 'R-073255',
          description: 'XTESTSCENARIOX-Erica0302_B',
          status: 'Completed',
          created: '2021-03-03T00:59:57.174855Z',
        },
        orderTest: {
          id: '2f039448-9379-4d59-9e6d-3e9af7334ef8',
          sampleName: 'ClonalityEChang0302_B',
          description: 'XTESTSCENARIOX-Erica0302_B',
          testType: 'ID',
          statusType: 'Completed',
        },
        specimen: {
          specimenNumber: 'SP-898117',
          collectionDate: '2018-08-16',
          externalSubjectId: 'SubjectIdEChang0302_B',
        },
        hasActiveReport: true,
        clinicalReport: {
          id: 'befed821-5d62-4ab3-9c8f-d4090adc524e',
          name: 'A32110791',
          documentType: 'CDx',
          status: 'Obsolete',
          externalSubjectId: 'SubjectIdEChang0302_B',
          orderTestId: '2f039448-9379-4d59-9e6d-3e9af7334ef8',
          created: '2021-03-03T01:06:33.062404Z',
        },
      },
    ],
  };
